// ClinicalDocumentation.tsx
import React from "react";
import { Paper, Text, Grid, Box, Stack, Group, Divider } from "@mantine/core";
import useColors from "../../../theme/useColors";

// Define types for our component props
interface DocumentationItem {
  label: string;
  value: string;
  date?: string;
}

interface DocumentationSection {
  title: string;
  items: DocumentationItem[];
}

interface ClinicalNote {
  title: string;
  items: string[];
}

interface ClinicalDocumentationProps {
  sections: DocumentationSection[];
  clinicalNotes?: ClinicalNote;
  summaryRationale?: string;
  showAiBulwarkLogo?: boolean;
  className?: string;
}

// Main component
const ClinicalDocumentation: React.FC<ClinicalDocumentationProps> = ({
  sections,
  clinicalNotes,
  summaryRationale,
  showAiBulwarkLogo = false,
  className,
}) => {
  const colors = useColors();

  const aiLogo = showAiBulwarkLogo ? (
    <Box
      component="img"
      src="/ai-bulwark-logo.svg"
      alt=""
      aria-hidden
      h={16}
      w="auto"
      style={{ flexShrink: 0, objectFit: "contain", marginTop: 4 }}
    />
  ) : null;

  const sectionTitleStyle = {
    fontFamily: "Roboto, sans-serif",
    fontSize: "14px",
    fontWeight: 900,
    lineHeight: "24px",
    color: colors.gray[8],
  } as const;

  return (
    <Paper
      className={className}
      px={"md"}
      radius='sm'
      style={{
        backgroundColor: colors.gray[2],
        border: `1px solid ${colors.gray[3]}`,
      }}
    >
      <Grid gutter={0}>
        <Grid.Col span={{ base: 12, md: 5.5 }}>
          <Stack gap={"2px"}>
            {(() => {
              let globalIndex = 0;
              return sections.map((section, sectionIndex) => (
                <Box key={`section-${sectionIndex}`}>
                  <Text style={sectionTitleStyle}>{section.title}</Text>

                  <Stack gap={"2px"}>
                    {section.items.map((item, itemIndex) => {
                      globalIndex += 1;
                      return (
                        <Group
                          key={`item-${sectionIndex}-${itemIndex}`}
                          style={{
                            display: "flex",
                            justifyContent: "space-between",
                          }}
                        >
                          <Text
                            style={{
                              fontFamily: "Roboto, sans-serif",
                              fontSize: "12px",
                              fontWeight: 400,
                              lineHeight: "20px",
                              color: colors.gray[8],
                              justifyContent: "flex-start",
                            }}
                          >
                            {globalIndex}
                            {". "}
                            {item.label}{" "}
                            {item?.value && (
                              <span>
                                = <strong>{item?.value}</strong>
                              </span>
                            )}
                          </Text>

                          {item.date && (
                            <Text
                              style={{
                                fontFamily: "Roboto, sans-serif",
                                fontSize: "12px",
                                fontWeight: 500,
                                lineHeight: "20px",
                                color: colors.gray[8],
                              }}
                            >
                              {item.date}
                            </Text>
                          )}
                        </Group>
                      );
                    })}
                  </Stack>
                </Box>
              ));
            })()}
          </Stack>
        </Grid.Col>

        {/* Divider */}
        <Grid.Col
          span={{ base: 0, md: 0.5 }}
          style={{ display: "flex", justifyContent: "center" }}
        >
          <Divider
            orientation='vertical'
            style={{ height: "100%", color: colors.gray[1] }}
          />
        </Grid.Col>

        {/* Right section (Clinical notes) */}
        <Grid.Col
          span={{ base: 12, md: 6 }}
          mt={{ base: "2px", md: 0, sm: "4px", xs: "2px" }}
        >
          {summaryRationale && (
            <Box mb="8px">
              <Group gap={6} align="flex-start" wrap="nowrap">
                {aiLogo}
                <Text style={sectionTitleStyle}>Summary</Text>
              </Group>
              <Text
                size="12px"
                lh="18px"
                fw={500}
                c={colors.danger[5]}
                mt="2px"
              >
                {summaryRationale}
              </Text>
            </Box>
          )}

          {clinicalNotes?.items?.length ? (
            <>
              <Text style={sectionTitleStyle}>
                {clinicalNotes?.title
                  ? clinicalNotes?.title
                  : `Relevant clinical documentation references are :`}
              </Text>

              <Box mb="2px">
                {clinicalNotes.items.map((text, i) => (
                  <Box
                    key={i}
                    mb={2}
                    style={{
                      fontFamily: "Roboto, sans-serif",
                      fontSize: "12px",
                      fontWeight: 400,
                      lineHeight: "20px",
                      color: colors.gray[8],
                      whiteSpace: "pre-wrap",
                    }}
                  >
                    {i + 1}.{" "}
                    <span
                      style={{ wordBreak: "break-word" }}
                      dangerouslySetInnerHTML={{ __html: text || "" }}
                    />
                    {showAiBulwarkLogo && (
                      <>
                        {" "}
                        <Box
                          component="img"
                          src="/ai-bulwark-logo.svg"
                          alt=""
                          aria-hidden
                          style={{
                            display: "inline-block",
                            height: 14,
                            width: "auto",
                            verticalAlign: "middle",
                          }}
                        />
                      </>
                    )}
                  </Box>
                ))}
              </Box>
            </>
          ) : null}
        </Grid.Col>
      </Grid>
    </Paper>
  );
};

export default ClinicalDocumentation;
